import React, { ChangeEvent, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { fetchProgramsAll } from "../../store/ProgramSlice";
import { AllPrograms } from "../../interfaces/Program";
import { debounce } from "lodash";
import { HealthCondition, fetchHealthData } from "../../store/UserSlice";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProgramCard from "../../components/ProgramCard";

export default function   ProgramsList() {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const [hId, sethId] = useState<string>(location.state?.id || "");
  const [selectedHealthConditions, setSelectedHealthConditions] = useState<string>("");
  const [searchValue, setSearchValue] = useState("");
  const [changeText, setChangeText] = useState(true);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const programsState = useSelector((state: RootState) => (state as any).programs);
  const programsAll: AllPrograms[] = programsState?.data || [];
  const loading: boolean = programsState?.loading || false;

  const healthConditions = useSelector((state: RootState) => {
    const d = (state as any).user?.data;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray((d as any).healthConditions)) return (d as any).healthConditions;
    return [];
  }) as HealthCondition[];

  useEffect(() => {
    const hIdCheckbox = document.getElementById(hId) as HTMLInputElement;
    if (hIdCheckbox) {
      hIdCheckbox.checked = true;
    }
  }, [hId]);

  useEffect(() => {
    dispatch(fetchHealthData());
    if (hId || !selectedHealthConditions) {
      dispatch(fetchProgramsAll({ search: searchValue, healthCondition: hId }));
    } else {
      dispatch(fetchProgramsAll({ search: searchValue, healthCondition: selectedHealthConditions }));
    }
  }, [dispatch, selectedHealthConditions, hId]);

  const handleSearch = (query: string) => {
    dispatch(fetchProgramsAll({ search: query, healthCondition: selectedHealthConditions }));
  };

  const debouncedSearch = useRef(debounce(handleSearch, 500)).current;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchValue(query);
    setChangeText(query === "");
    debouncedSearch(query);
  };

  const handleHealthConditionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const healthConditionName = e.target.name;
    sethId("");
    const hIdCheckbox = document.getElementById(hId) as HTMLInputElement;
    if (hIdCheckbox) {
      hIdCheckbox.checked = false;
    }

    if (e.target.id === "all") {
      const allHealthConditions = document.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
      setSelectedHealthConditions("");
      allHealthConditions.forEach((condition) => {
        condition.checked = false;
      });
      const allCheckbox = document.getElementById("all") as HTMLInputElement;
      if (allCheckbox) {
        allCheckbox.checked = true;
      }
      dispatch(fetchProgramsAll({ search: searchValue, healthCondition: "" }));
    } else {
      if (e.target.checked) {
        setSelectedHealthConditions((prev) =>
          prev ? prev + "," + healthConditionName : healthConditionName
        );
        const allCheckbox = document.getElementById("all") as HTMLInputElement;
        if (allCheckbox) {
          allCheckbox.checked = false;
        }
      } else {
        setSelectedHealthConditions((prev) =>
          prev ? prev.replace(new RegExp(healthConditionName + ",?\\s?"), "") : ""
        );
      }
    }
  };

  const handleResetFilters = () => {
    setSearchValue("");
    setChangeText(true);
    sethId("");
    setSelectedHealthConditions("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    const allCheckboxes = document.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
    allCheckboxes.forEach((cb) => {
      cb.checked = false;
    });
    const allCheckbox = document.getElementById("all") as HTMLInputElement;
    if (allCheckbox) {
      allCheckbox.checked = true;
    }
    dispatch(fetchProgramsAll({ search: "", healthCondition: "" }));
  };

  const handleButtonClick = () => {
    setDropdownVisible(!dropdownVisible);
  };

  useEffect(() => {
    localStorage.removeItem("SetSlug");
  }, []);

  const hasActiveFilters = Boolean(searchValue || selectedHealthConditions || hId);

  return (
    <>
      <div className="relative">
        <div
          className="bg-cover bg-center text-center text-white p-8"
          style={{ backgroundImage: `url("./images/innerb.jpg")` }}
        >
          <div className="text-center pt-40 pb-28">
            <h1 className="text-2xl md:text-4xl font-bold">{t("programs.bannerTitle")}</h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto my-10 px-4 md:px-6">
        {/* Search & Filter Bar: Only show if data exists OR if user has applied filters/search */}
        {(programsAll.length > 0 || hasActiveFilters) && (
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <form className="items-start w-auto" onSubmit={(e) => e.preventDefault()}>
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  placeholder=""
                  onChange={handleChange}
                  className="border rounded-md py-2 w-60 md:w-96 px-4 focus:outline-none placeholder-[#B0BEC5] bg-[#F5F7F8]"
                />
                {changeText && (
                  <div className="absolute inset-y-0 left-0 pt-1 pl-4 flex items-center pointer-events-none">
                    <img src="./images/Vector.png" className="mx-auto mb-1" width={18} alt="search" />
                    <span className="pl-2 leading-3 text-[#B0BEC5]">{t("programs.search")}</span>
                  </div>
                )}
              </div>
            </form>

            <div className="relative">
              <button
                id="dropdownDefault"
                data-dropdown-toggle="dropdown"
                className="border rounded-md py-2 text-[#263238] px-4 focus:outline-none placeholder-[#B0BEC5] bg-[#F5F7F8] hover:font-semibold flex items-center cursor-pointer"
                type="button"
                onClick={handleButtonClick}
              >
                {t("programs.filter")}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline ml-4"
                >
                  <path d="M8.23461 15.6045H1.00049" stroke="#263238" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path fillRule="evenodd" clipRule="evenodd" d="M19.2353 15.6045C19.2353 17.195 17.9459 18.4845 16.3553 18.4845C14.7648 18.4845 13.4753 17.195 13.4753 15.6045C13.4753 14.0127 14.7648 12.7245 16.3553 12.7245C17.9459 12.7245 19.2353 14.0127 19.2353 15.6045Z" stroke="#263238" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12.0001 3.88H19.2354" stroke="#263238" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path fillRule="evenodd" clipRule="evenodd" d="M1 3.88C1 5.47176 2.28941 6.76 3.88 6.76C5.47059 6.76 6.76 5.47176 6.76 3.88C6.76 2.28941 5.47059 1 3.88 1C2.28941 1 1 2.28941 1 3.88Z" stroke="#263238" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {dropdownVisible && (
                <div id="dropdown" className="absolute left-0 z-20 w-56 p-4 mt-2 bg-white rounded-md shadow-lg border border-gray-100">
                  <ul className="space-y-4 text-sm" aria-labelledby="dropdownDefault">
                    <li className="flex items-center">
                      <input
                        id="all"
                        type="checkbox"
                        value=""
                        onChange={handleHealthConditionChange}
                        className="w-5 h-5 bg-[#B4804A] border-[#A5A6A9] rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
                      />
                      <label className="ml-3 text-base font-medium text-gray-900 cursor-pointer" htmlFor="all">
                        {t("programs.all")}
                      </label>
                    </li>
                    {healthConditions.map((healthCondition: HealthCondition) => (
                      <li className="flex items-center" key={healthCondition.id}>
                        <input
                          id={healthCondition.id}
                          name={healthCondition.id}
                          type="checkbox"
                          value=""
                          className="w-5 h-5 bg-gray-100 border-[#A5A6A9] rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
                          onChange={handleHealthConditionChange}
                        />
                        <label className="ml-3 text-base font-medium text-[#263542] capitalize cursor-pointer" htmlFor={healthCondition.id}>
                          {healthCondition.name}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="text-sm font-semibold text-[#037897] hover:text-[#025a72] underline transition-colors cursor-pointer"
              >
                {t("programs.resetFilters", "Reset filters")}
              </button>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="bg-white rounded-lg border border-gray-100 p-4 animate-pulse shadow-sm h-80 flex flex-col justify-between">
                <div className="bg-gray-200 h-44 rounded-md w-full mb-3" />
                <div className="space-y-2">
                  <div className="bg-gray-200 h-4 rounded w-3/4" />
                  <div className="bg-gray-200 h-3 rounded w-1/2" />
                </div>
                <div className="bg-gray-200 h-8 rounded mt-3" />
              </div>
            ))}
          </div>
        )}

        {/* Programs Grid */}
        {!loading && programsAll.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {programsAll.map((programs: AllPrograms) => (
              <div key={programs.id} className="h-full">
                <ProgramCard program={programs} />
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && programsAll.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 sm:p-12 text-center max-w-2xl mx-auto my-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#037897]/10 text-[#037897] flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-[#263238] mb-2 font-gilroy">
              {t("programs.noProgramsFound", "No programs available at the moment")}
            </h3>

            <p className="text-gray-500 text-sm sm:text-base leading-relaxed mb-6 max-w-lg mx-auto">
              {hasActiveFilters
                ? t(
                    "programs.noProgramsFiltered",
                    "No programs match your search or selected filters. Try clearing your filters or search term."
                  )
                : t(
                    "programs.noProgramsDefault",
                    "We are currently preparing new programs. Please check back soon or contact us to be notified when new sessions open."
                  )}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3">
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  className="bg-[#037897] hover:bg-[#02627c] text-white font-semibold py-2.5 px-5 rounded-md text-sm transition-colors shadow-sm cursor-pointer"
                >
                  {t("programs.resetFilters", "Reset filters")}
                </button>
              )}
              <Link
                to="/contact"
                className="border border-[#037897] text-[#037897] hover:bg-[#037897]/5 font-semibold py-2.5 px-5 rounded-md text-sm transition-colors"
              >
                {t("programs.contactUs", "Contact Us")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}


